import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  FormStation,
  type ObjectSchemaDefinition,
  SingleLineTextField,
  type StationMessage,
  getFormDiff,
} from '@axinom/mosaic-ui';
import { Field, useFormikContext } from 'formik';
import { isEmpty } from 'lodash';
import React, { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { config } from '../../../../config';
import { useCreateServiceDeploymentMutation } from '../../../../generated/hosting-service';
import type { BreadcrumbResolver } from '../../../../models/breadcrumb';
import { environmentApolloClient } from '../../../environmentApolloClient';
import type { LoadDataType } from './CustomizableServiceDeploymentCreate.types';
import { RowSelectField } from './RowSelectField/RowSelectField';

const deploymentValidation = Yup.object<ObjectSchemaDefinition>({
  name: Yup.string().required('Name is required'),
  containerImage: Yup.array().min(
    1,
    'Container Image field must have at least 1 item',
  ),
  serviceDeploymentManifest: Yup.array().min(
    1,
    'Service Deployment Configuration field must have at least 1 item',
  ),
});

export const CustomizableServiceDeploymentCreate: React.FC = () => {
  const [data, setData] = useState<LoadDataType>();
  const [isDeployDisabled, setIsDeployDisabled] = useState<boolean>(true);
  const [stationMessage, setStationMessage] = useState<StationMessage>();
  const history = useHistory();

  const { envId, customizableServiceId } = useParams<{
    envId: string;
    customizableServiceId: string;
  }>();

  const apolloClient = environmentApolloClient(
    envId,
    new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
  );

  const [initiateServiceDeployment] = useCreateServiceDeploymentMutation({
    client: apolloClient,
    fetchPolicy: 'no-cache',
  });

  const UpdateFormikValues = (): null => {
    // Grab values and submitForm from context
    const { values, initialValues, submitForm } =
      useFormikContext<LoadDataType>();
    React.useEffect(() => {
      setData(values);
      const formDiff = getFormDiff(values, initialValues);
      setIsDeployDisabled(
        isEmpty(formDiff) ||
          !(
            values.name !== '' &&
            'containerImage' in values &&
            values.containerImage.length > 0 &&
            'serviceDeploymentManifest' in values &&
            values.serviceDeploymentManifest.length > 0
          ),
      );
    }, [values, submitForm, initialValues]);
    return null;
  };

  return (
    <>
      <FormStation
        defaultTitle={'New Deployment'}
        subtitle={'Create Deployment'}
        validationSchema={deploymentValidation}
        cancelNavigationUrl={`/env/${envId}/services/${ManagedServiceId.HOSTING_SERVICE}/config/customizable-services/${customizableServiceId}/deployments/`}
        saveData={() => {
          // We don't need to save the data, since this station only displays the validation status.
        }}
        initialData={{
          data: {
            name: ``,
            containerImage: [],
            serviceDeploymentManifest: [],
            servicePiletArtifacts: [],
          } as LoadDataType,
          loading: false,
          error: undefined,
        }}
        showSaveHeaderAction={false}
        actions={[
          {
            label: 'Deploy',
            onActionSelected: async () => {
              const result = await initiateServiceDeployment({
                variables: {
                  input: {
                    name: data?.name,
                    serviceDefinitionId: customizableServiceId,
                    containerImageVersion:
                      data?.containerImage &&
                      data?.containerImage[0]?.containerImageVersion
                        ? data?.containerImage[0]?.containerImageVersion.split(
                            ':',
                          )[1]
                        : '',
                    serviceDeploymentConfigurationId:
                      data?.serviceDeploymentManifest
                        ? data?.serviceDeploymentManifest[0]?.id
                        : '',
                    servicePiletArtifactIds:
                      data?.servicePiletArtifacts?.map(
                        (piletArtifact) => piletArtifact?.id,
                      ) ?? [],
                  },
                },
              });

              if (result.errors !== undefined) {
                setStationMessage({
                  type: 'error',
                  title: result.errors[0]?.message,
                });
              } else {
                history.push(
                  `/env/${envId}/services/${ManagedServiceId.HOSTING_SERVICE}/config/customizable-services/${customizableServiceId}/deployments/${result.data?.initiateServiceDeployment.serviceDeploymentId}`,
                );
              }
            },
            confirmationMode: 'Advanced',
            confirmationConfig: {
              body: `Continuing will initiate a new deployment of the service with the selected configuration. Please confirm to proceed.`,
            },
            isDisabled: isDeployDisabled,
          },
        ]}
        alwaysSubmitBeforeAction={true}
        stationMessage={stationMessage}
      >
        <UpdateFormikValues />
        <Field name="name" label="Name" as={SingleLineTextField} />
        <Field
          name="containerImage"
          label="Container Image"
          type="ContainerImage"
          max={1}
          as={RowSelectField}
        />
        <Field
          name="servicePiletArtifacts"
          label="Pilet Artifact(s)"
          type="ServicePiletArtifacts"
          as={RowSelectField}
        />
        <Field
          name="serviceDeploymentManifest"
          label="Deployment Manifest"
          type="ServiceDeploymentManifest"
          max={1}
          as={RowSelectField}
        />
      </FormStation>
    </>
  );
};

export const CustomizableServiceDeploymentCreateCrumb: BreadcrumbResolver =
  () => 'New';
